import { api } from "@/common/http/axios";

/**
 * 仿真看板（`/sim-dashboard/*`）API。
 *
 * 全平台运营 dashboard 三块数据，全部读非分区汇总表 job_stat：
 * - summary：顶部 KPI，累计任务数/里程 + 今日增量。
 * - trend：  日/周/月/累计分桶的任务数 + 里程序列。
 * - ranking：按执行人聚合的任务数/里程排行榜。
 *
 * 里程字段单位统一为「米」（后端不换算），前端展示层自行转 km。
 * 权限口径：summary / trend 公开（嵌入概览页）；ranking 受 `menu:sim-dashboard`
 * 权限点保护（后端 per-route require_permission）。
 */

export interface DashboardSummaryGroupVo {
  /** 分组键：planning（PNC）/ perception（感知） */
  group_key: string;
  /** 该组历史累计任务数 */
  total_task_num: number;
  /** 该组历史累计里程，单位：米 */
  total_mileage: number;
  today_task_num: number;
  today_mileage: number;
}

export interface DashboardSummaryVo {
  /** 历史累计任务数 = SUM(total_task_num) */
  total_task_num: number;
  /** 历史累计里程，单位：米 */
  total_mileage: number;
  today_task_num: number;
  today_mileage: number;
  /** 按 PNC / 感知拆分的分组明细（顶层字段 = 各组之和） */
  groups: DashboardSummaryGroupVo[];
}

export interface DashboardSummaryResponse {
  as_of: string;
  summary: DashboardSummaryVo;
}

export type TrendGranularity = "day" | "week" | "month" | "all";

export interface DashboardTrendPoint {
  /** 桶标签：day→YYYY-MM-DD / week→YYYY-Www / month→YYYY-MM / all→"all" */
  bucket: string;
  task_num: number;
  /** 单位：米 */
  mileage: number;
}

export interface DashboardTrendResponse {
  as_of: string;
  granularity: TrendGranularity;
  points: DashboardTrendPoint[];
}

export type RankingMetric = "task" | "mileage";

/** 排行榜仿真类型过滤：全部 / PNC / 感知。 */
export type RankingSimType = "all" | "planning" | "perception";

/** 排行榜统计时间窗：今日 / 最近 7 天 / 本周 / 本月 / 历史累计。 */
export type RankingTimeRange = "day" | "7d" | "week" | "month" | "all";

export interface DashboardRankingRow {
  rank: number;
  /** 执行人身份锚（SSO email/user_key），保留作唯一标识、防重名歧义。 */
  creator: string;
  /** 执行人中文名（展示用）；后端按 email 查 sys_user.name，命不到回退 creator。 */
  creator_name: string;
  task_num: number;
  /** 单位：米 */
  mileage: number;
}

export interface DashboardRankingResponse {
  as_of: string;
  metric: RankingMetric;
  sim_type: RankingSimType;
  time_range: RankingTimeRange;
  rows: DashboardRankingRow[];
}

export const getDashboardSummary = async (): Promise<DashboardSummaryResponse> => {
  const response = await api.get("/sim-dashboard/summary");
  return response.data?.data as DashboardSummaryResponse;
};

export const getDashboardTrend = async (
  granularity: TrendGranularity = "day",
  periods: number = 30,
): Promise<DashboardTrendResponse> => {
  const response = await api.get("/sim-dashboard/trend", {
    params: { granularity, periods },
  });
  return response.data?.data as DashboardTrendResponse;
};

export const getDashboardRanking = async (
  metric: RankingMetric = "task",
  limit: number = 0,
  simType: RankingSimType = "all",
  timeRange: RankingTimeRange = "7d",
): Promise<DashboardRankingResponse> => {
  const response = await api.get("/sim-dashboard/ranking", {
    params: { metric, limit, sim_type: simType, time_range: timeRange },
  });
  return response.data?.data as DashboardRankingResponse;
};
